import { useState } from 'react';

import { Button, Notice } from '@/components/PageHeading';
import type { BookingDraft } from '@core/bookings/draft';
import { holdChoices, hoursWord } from '@core/bookings/newBookingSteps';
import { holdHoursProblem } from '@core/bookings/stageMoves';

import { Choice, Dialog } from '../Dialog';

type Instead = 'Hold' | 'Quotation' | 'Enquiry';

/**
 * "Not confirming yet?" — the three other ways a booking can start, behind a link under Confirm
 * rather than four equal choices on every booking, because nearly every booking is confirmed. The
 * phone's `HoldQuoteSheet`. A hold takes the garments off the rail until it lapses and takes no
 * number; a quote gets a QT number and holds nothing; an enquiry only notes who asked. None takes
 * money.
 */
export function HoldQuoteDialog({
  draft,
  busy,
  onTake,
  onClose,
}: {
  draft: BookingDraft;
  busy: boolean;
  onTake: (draft: BookingDraft) => void;
  onClose: () => void;
}) {
  const [instead, setInstead] = useState<Instead>('Hold');
  const [hours, setHours] = useState(draft.holdHours);
  const problem = instead === 'Hold' ? holdHoursProblem(hours) : null;

  const options: { value: Instead; title: string; body: string }[] = [
    {
      value: 'Hold',
      title: 'Hold the garments',
      body: 'Nobody else can book them until the hold runs out. No number, no money.',
    },
    {
      value: 'Quotation',
      title: 'Send a quote',
      body: 'Gets a QT number to send on WhatsApp. Holds nothing, so the same suit can be quoted to others.',
    },
    {
      value: 'Enquiry',
      title: 'Just note the enquiry',
      body: 'Keeps who asked and for what, to call back later.',
    },
  ];

  return (
    <Dialog
      title="Not confirming yet?"
      width={520}
      onClose={onClose}
      footer={
        <Button
          variant="primary"
          shortcut="save"
          className="ms-auto"
          disabled={busy || problem !== null}
          onClick={() =>
            onTake({
              ...draft,
              startAs: instead,
              ...(instead === 'Hold' ? { holdHours: hours } : {}),
            })
          }
        >
          {instead === 'Hold'
            ? `Hold for ${hoursWord(hours)}`
            : instead === 'Quotation'
              ? 'Save the quote'
              : 'Save the enquiry'}
        </Button>
      }
    >
      <div role="radiogroup" className="flex flex-col gap-2">
        {options.map((option) => {
          const chosen = instead === option.value;

          return (
            <div
              key={option.value}
              className={`rounded-md border p-3 ${
                chosen ? 'border-primary bg-primary-transparent' : 'border-border-color bg-white'
              }`}
            >
              <button
                type="button"
                role="radio"
                aria-checked={chosen}
                onClick={() => setInstead(option.value)}
                className="w-full cursor-pointer text-start"
              >
                <span className="block text-[13.5px] font-semibold text-gray-900">
                  {option.title}
                </span>
                <span className="text-default block text-[12px]">{option.body}</span>
              </button>

              {chosen && option.value === 'Hold' && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {holdChoices.map((choice) => (
                    <Choice
                      key={choice}
                      label={hoursWord(choice)}
                      chosen={choice === hours}
                      onChoose={() => setHours(choice)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {problem && <Notice tone="warning" title={problem} />}
    </Dialog>
  );
}
